import styled from '@emotion/styled';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { config } from 'src/modules/insuranceIntegration/config/config';
import { getMyTherapists } from 'src/modules/providers/selectors/providersSelectors';
import { color, font } from 'src/shared/styles/variables';

import { Button } from 'src/shared/components/Button';
import { CyclistSVG } from '../svg/CyclistSVG';
import { navigationService } from 'src/config/rootService';
import { LimitedTimeOfferModal } from '../components/LimitedTimeOfferModal';

export enum InsuranceApprovedPageTestID {
    Wrapper = 'InsuranceApprovedPageTestID.Wrapper',
    DashboardButton = 'InsuranceApprovedPageTestID.DashboardButton',
    BookButton = 'InsuranceApprovedPageTestID.BookButton',
    LimitedTimeOfferModal = 'InsuranceApprovedPageTestID.LimitedTimeOfferModal',
}

const FormBoundary = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: 520,
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: '2.75rem',
    textAlign: 'center',
});

const IconWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
});
const PageTitle = styled('h1')({ color: color.ebonyClay }, font.heavy(font.size.extraLarge));
const PageDescription = styled('p')(
    {
        color: color.blueBayoux,
        marginBottom: 28,
    },
    font.light(font.size.medium),
);
const Link = styled.a(
    {
        color: color.royalBlue,
        textDecoration: 'underline',
    },
    font.heavy(font.size.base),
);
const PageLinks = styled('p')(
    {
        color: color.blueBayoux,
        marginBottom: 28,
    },
    font.light(font.size.medium),
);

const ButtonsRow = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 24,
    gap: 16,
});

export const InsuranceApprovedPage = () => {
    const { t } = useTranslation();
    const myTherapists = useSelector(getMyTherapists);
    const { alwaysShowHpiLimitedTimeOfferModal } = useFlags();

    const handleBookCoachingSessionClick = () => {
        switch (myTherapists.length) {
            case 1:
                navigationService.navigate('/schedule-session/calendar', {
                    coachUuid: myTherapists[0].uuid,
                    isFirstSession: false,
                    disableBack: true,
                });
                break;
            case 0:
                navigationService.navigate('/dashboard');
                break;
            default:
                navigationService.navigate('/schedule-session/select-coach');
                break;
        }
    };

    return (
        <FormBoundary data-testid={InsuranceApprovedPageTestID.Wrapper}>
            <IconWrapper>
                <CyclistSVG width={277} height={235} />
            </IconWrapper>
            <PageTitle>{t('insuranceForm:insurance_approved.page_title')}</PageTitle>
            <PageDescription>
                {t('insuranceForm:insurance_approved.page_description')}
            </PageDescription>
            <PageLinks>
                <Trans
                    t={t}
                    ns="insuranceForm"
                    i18nKey="insurance_approved.page_links"
                    components={[
                        <Link
                            key={`${config.faqUrl}_${new Date().getTime()}`}
                            onClick={() => window.open(`${config.faqUrl}`, '_blank')}
                        />,
                        <Link
                            key={`${config.claimsMail}_${new Date().getTime()}`}
                            onClick={() => window.open(`mailto:${config.claimsMail}`, '_blank')}
                        />,
                    ]}
                />
            </PageLinks>
            <ButtonsRow>
                <Button
                    fullWidth
                    type="white"
                    testID={InsuranceApprovedPageTestID.DashboardButton}
                    onClick={() => navigationService.navigate('/dashboard')}
                >
                    {t('insuranceForm:insurance_approved.back_home')}
                </Button>
                <Button
                    fullWidth
                    testID={InsuranceApprovedPageTestID.BookButton}
                    onClick={handleBookCoachingSessionClick}
                >
                    {t('insuranceForm:insurance_approved.book_session')}
                </Button>
            </ButtonsRow>
            {alwaysShowHpiLimitedTimeOfferModal && (
                <LimitedTimeOfferModal
                    testID={InsuranceApprovedPageTestID.LimitedTimeOfferModal}
                    onClick={handleBookCoachingSessionClick}
                />
            )}
        </FormBoundary>
    );
};
